import { SurveyAnswer, SurveyPresentation, SurveyResultEvent } from './types';
import { hasOnly, isIdentifier, isRecord, isRevision, parseSurveyEvent, utf8Size, validAnswer, validPresentation } from './validation';

export interface SurveyRecoveryItem {
  eventId: string;
  presentation: SurveyPresentation;
  result?: SurveyResultEvent['payload'] | null;
  helpExchange?: {
    interactionId: string;
    formId: string;
    presentationRevision: number;
    mode: 'configured' | 'generative';
    interactionKind: 'requestHelp' | 'governedSurveyHelp';
    memberQuestion?: string | null;
    mayaReply: string;
  };
  isCurrent: boolean;
  pendingInteractionId?: string;
  pendingKind?: 'surveyAnswer' | 'surveyAction' | 'governedSurveyHelp';
  provisionalAnswer?: SurveyAnswer;
  recoverySequence: number;
}

export interface SurveyRecovery {
  schemaVersion: 1;
  chatSessionId: string;
  items: SurveyRecoveryItem[];
}

export const isSurveyRouteId = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) &&
  value !== '00000000-0000-0000-0000-000000000000';

export const surveyCanonical = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(surveyCanonical).join(',')}]`;
  if (isRecord(value)) return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${surveyCanonical(value[key])}`).join(',')}}`;
  return JSON.stringify(value);
};

const validHistoricalPresentation = (value: unknown): value is SurveyPresentation => {
  if (validPresentation(value)) return true;
  if (!isRecord(value) || !Array.isArray(value.footerActions) || value.footerActions.length !== 0) return false;
  const action = value.messageType === 'surveyQuestion' ? 'submitAnswer' :
    value.messageType === 'surveyRoute' ? 'selectRoute' :
      value.messageType === 'surveyConsent' ? 'decideConsent' : undefined;
  return !!action && validPresentation({ ...value, footerActions: [
    { action, enabled: true },
    ...(isRecord(value.help) && value.help.mode === 'configured' ? [{ action: 'requestHelp' as const, label: 'Help',
      enabled: isRecord(value.help) && value.help.available === true }] : []),
  ] });
};

const validRecoveryPresentation = (value: unknown, isCurrent: boolean): value is SurveyPresentation =>
  isCurrent ? validPresentation(value) : validHistoricalPresentation(value);

export const parseSurveyRecovery = (input: unknown, sessionId: string): SurveyRecovery | undefined => {
  try {
    if (!isRecord(input) || utf8Size(JSON.stringify(input)) > 4 * 1024 * 1024 ||
        !hasOnly(input, ['schemaVersion', 'chatSessionId', 'items']) || input.schemaVersion !== 1 ||
        !isSurveyRouteId(input.chatSessionId) || input.chatSessionId !== sessionId ||
        !Array.isArray(input.items) || input.items.length > 1000) return undefined;
    if (!input.items.every((item) => isRecord(item) && isRevision(item.recoverySequence))) return undefined;
    const orderedItems = [...input.items].sort((left, right) =>
      Number((left as Record<string, unknown>).recoverySequence) - Number((right as Record<string, unknown>).recoverySequence));
    const items: SurveyRecoveryItem[] = [];
    const eventIds = new Set<string>();
    const sequences = new Set<number>();
    for (const [index, item] of orderedItems.entries()) {
      const isFinal = index === orderedItems.length - 1;
        if (!isRecord(item) || !hasOnly(item, ['eventId', 'presentation', 'result', 'helpExchange', 'isCurrent', 'pendingInteractionId', 'pendingKind', 'provisionalAnswer', 'recoverySequence']) ||
          !isIdentifier(item.eventId) || !validRecoveryPresentation(item.presentation, isFinal) ||
          typeof item.isCurrent !== 'boolean' || !isRevision(item.recoverySequence)) return undefined;
      if (('pendingInteractionId' in item) !== ('pendingKind' in item) ||
          ('pendingInteractionId' in item && (!isSurveyRouteId(item.pendingInteractionId) ||
            !['surveyAnswer', 'surveyAction', 'governedSurveyHelp'].includes(String(item.pendingKind))))) return undefined;
          if (item.isCurrent !== isFinal || !isFinal && 'pendingInteractionId' in item) return undefined;
      if ('result' in item) {
        if (item.result !== null && !isRecord(item.result)) return undefined;
      }
      if (isRecord(item.result)) {
        const event = parseSurveyEvent({ schemaVersion: 1, target: 'interactive', messageType: 'surveyInteractionResult',
          chatSessionId: sessionId, responseId: item.result.interactionId, payload: item.result });
        if (!event || event.messageType !== 'surveyInteractionResult' ||
            event.payload.formId !== item.presentation.formId ||
            event.payload.presentationRevision !== item.presentation.presentationRevision ||
            event.payload.status !== 'replayed') return undefined;
      }
          if ('helpExchange' in item) {
            const exchange = item.helpExchange;
            if (!isRecord(exchange) || !hasOnly(exchange, ['interactionId', 'formId', 'presentationRevision', 'mode', 'interactionKind', 'memberQuestion', 'mayaReply']) ||
            !isSurveyRouteId(exchange.interactionId) || exchange.formId !== item.presentation.formId ||
            exchange.presentationRevision !== item.presentation.presentationRevision ||
            !['configured', 'generative'].includes(String(exchange.mode)) || !isIdentifier(exchange.mayaReply) ||
            !['requestHelp', 'governedSurveyHelp'].includes(String(exchange.interactionKind)) ||
            exchange.interactionKind === 'requestHelp' && 'memberQuestion' in exchange && exchange.memberQuestion !== null ||
            exchange.interactionKind === 'governedSurveyHelp' && !isIdentifier(exchange.memberQuestion)) return undefined;
          }
            if ('provisionalAnswer' in item && (!isFinal || !validAnswer(item.provisionalAnswer) ||
                item.provisionalAnswer.type !== 'freeText' || item.presentation.inputType !== 'freeText')) return undefined;
            if (!isFinal && (!(item.result as SurveyResultEvent['payload'] | null | undefined)?.acceptedAnswer && !item.helpExchange) ||
              isFinal && item.result != null || eventIds.has(item.eventId) || sequences.has(item.recoverySequence)) return undefined;
      eventIds.add(item.eventId);
      sequences.add(item.recoverySequence);
      items.push(item as unknown as SurveyRecoveryItem);
    }
    if (!items.length) return undefined;
    return JSON.parse(JSON.stringify({ ...input, items }));
  } catch {
    return undefined;
  }
};